import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { clientRectToLayoutRect } from '../../utils/pageZoom';

export type ProgramSlideTrashDeleteControlProps = {
  isHolding: boolean;
  showConfirm: boolean;
  disabled?: boolean;
  ariaLabel: string;
  title?: string;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerUp: () => void;
  onPointerLeave: () => void;
  onPointerCancel: () => void;
  onCancelConfirm: () => void;
  onConfirmDelete: () => void;
};

/** 짧게 클릭 시 휴지통 위쪽에 취소·삭제 미니 확인(툴바 행 밖) */
export function ProgramSlideTrashDeleteControl({
  isHolding,
  showConfirm,
  disabled = false,
  ariaLabel,
  title = '짧게 누르면 확인 후 삭제 · 길게 누르면 바로 삭제',
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
  onCancelConfirm,
  onConfirmDelete,
}: ProgramSlideTrashDeleteControlProps) {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const [floatPos, setFloatPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!showConfirm) {
      setFloatPos(null);
      return;
    }
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const rect = clientRectToLayoutRect(el.getBoundingClientRect());
      setFloatPos({
        left: rect.right,
        top: rect.top,
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [showConfirm]);

  const confirmPortal =
    showConfirm && floatPos && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="cp-program-slide-canvas-trash-confirm-float"
            role="group"
            aria-label="삭제 확인"
            style={{
              left: `${Math.round(floatPos.left)}px`,
              top: `${Math.round(floatPos.top)}px`,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="cp-program-slide-canvas-trash-confirm-btn cp-program-slide-canvas-trash-confirm-btn--cancel"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCancelConfirm();
              }}
            >
              취소
            </button>
            <button
              type="button"
              className="cp-program-slide-canvas-trash-confirm-btn cp-program-slide-canvas-trash-confirm-btn--delete"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onConfirmDelete();
              }}
            >
              삭제
            </button>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <span className="cp-program-slide-canvas-trash-wrap" ref={wrapRef}>
        <button
          type="button"
          className={'cp-program-slide-canvas-trash' + (isHolding ? ' is-holding' : '')}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerLeave}
          onPointerCancel={onPointerCancel}
          disabled={disabled}
          aria-label={ariaLabel}
          title={title}
          aria-expanded={showConfirm}
        >
          <img src="/Trash.svg" alt="" aria-hidden className="cp-program-slide-canvas-trash-icon" />
        </button>
      </span>
      {confirmPortal}
    </>
  );
}
